import React, { createContext, useContext, useState, useCallback } from 'react';
import { DesignSettings } from '../types/DesignSettings';
import { DEFAULT_DESIGN_SETTINGS } from '../constants/defaultDesignSettings';
import {
  CLASSIC_DEFAULTS,
  STRIP_DEFAULTS,
  HORIZONTAL_DEFAULTS,
  WIDE_DEFAULTS,
  COMPACT_DEFAULTS,
  STACKED_DEFAULTS,
  CORPORATE_DEFAULTS,
  SOCIAL_DEFAULTS,
  CENTRAL_DEFAULTS,
  FOOTER_DEFAULTS,
  TOPBAR_DEFAULTS,
  RIGHTSIDE_DEFAULTS
} from '../constants/templateDefaults';

const TEMPLATE_COLOR_WHITE = ['horizontal', 'compact', 'central'];

const enforceTemplateThemeDefaults = (template: string, defaults: DesignSettings): DesignSettings => {
  const clonedDefaults: DesignSettings = JSON.parse(JSON.stringify(defaults));

  if (TEMPLATE_COLOR_WHITE.includes(template)) {
    clonedDefaults.themeColor = '#ffffff';
    clonedDefaults.socialBanner = {
      ...(clonedDefaults.socialBanner || {}),
      backgroundColor: '#ffffff'
    };
  }

  return clonedDefaults;
};

interface DesignSettingsContextType {
  settings: DesignSettings;
  updateSettings: (settings: Partial<DesignSettings>) => void;
  resetSettings: () => void;
  setTemplateDefaults: (template: string) => void;
  setTemplateDefaultsPreserveContent: (template: string) => void;
}

const DesignSettingsContext = createContext<DesignSettingsContextType | undefined>(undefined);

export const useDesignSettings = () => {
  const context = useContext(DesignSettingsContext);
  if (!context) {
    throw new Error('useDesignSettings must be used within a DesignSettingsProvider');
  }
  return context;
};

export const DesignSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<DesignSettings>(CLASSIC_DEFAULTS);
  const [currentTemplate, setCurrentTemplate] = useState<string>('classic');
  
  console.log('CONTEXT_INIT - DesignSettingsProvider initialized');
  console.log('CONTEXT_INIT - Initial settings:', CLASSIC_DEFAULTS);
  console.log('CONTEXT_INIT - Initial settings layoutType:', CLASSIC_DEFAULTS.layoutType);
  console.log('CONTEXT_INIT - Initial settings themeColor:', CLASSIC_DEFAULTS.themeColor);
  console.log('CONTEXT_INIT - Initial settings socialMediaIconType:', CLASSIC_DEFAULTS.socialMediaIconType);
  console.log('CONTEXT_INIT - Initial currentTemplate:', 'classic');

  const updateSettings = useCallback((newSettings: Partial<DesignSettings>) => {
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - updateSettings called with:', newSettings);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Current settings before update:', settings);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Current layoutType:', settings.layoutType);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Current themeColor:', settings.themeColor);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Current socialMediaIconType:', settings.socialMediaIconType);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - New layoutType:', newSettings.layoutType);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - New themeColor:', newSettings.themeColor);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - New socialMediaIconType:', newSettings.socialMediaIconType);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - farewellSignature in newSettings:', newSettings.farewellSignature);
    console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - farewellSignature in current settings:', settings.farewellSignature);
    
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      
      // Clean up any legacy signOffSignAs field from farewellSignature
      if (updated.farewellSignature && 'signOffSignAs' in updated.farewellSignature) {
        console.log('🧹 Cleaning up legacy signOffSignAs field from farewellSignature');
        const { signOffSignAs, ...cleanFarewellSignature } = updated.farewellSignature;
        updated.farewellSignature = cleanFarewellSignature;
        console.log('🧹 Cleaned farewellSignature:', cleanFarewellSignature);
      }
      
      if (newSettings.themeColor && newSettings.themeColor !== prev.themeColor) {
        // Special handling for theme color changes
      }
      
      console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Updated settings:', updated);
      console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Updated layoutType:', updated.layoutType);
      console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Updated themeColor:', updated.themeColor);
      console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - Updated socialMediaIconType:', updated.socialMediaIconType);
      console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - farewellSignature in updated settings:', updated.farewellSignature);
      console.log('⚙️⚙️⚙️ SETTINGS_UPDATE - State update completed - new farewellSignature:', updated.farewellSignature);
      return updated;
    });
  }, [settings]);

  const resetSettings = useCallback(() => {
    setSettings(prev => {
      switch (currentTemplate) {
        case 'classic':
          return CLASSIC_DEFAULTS;
        case 'strip':
          return STRIP_DEFAULTS;
        case 'horizontal':
          return HORIZONTAL_DEFAULTS;
        case 'wide':
          return WIDE_DEFAULTS;
        case 'compact':
          return COMPACT_DEFAULTS;
        case 'stacked':
          return STACKED_DEFAULTS;
        case 'corporate':
          return {
            ...CORPORATE_DEFAULTS,
            themeColor: prev.themeColor
          };
        case 'social':
          return SOCIAL_DEFAULTS;
        case 'central':
          return CENTRAL_DEFAULTS;
        case 'footer':
          return {
            ...FOOTER_DEFAULTS,
            themeColor: prev.themeColor
          };
        case 'topbar':
          return TOPBAR_DEFAULTS;
        case 'rightside':
          return RIGHTSIDE_DEFAULTS;
        default:
          return {
            ...DEFAULT_DESIGN_SETTINGS,
            themeColor: prev.themeColor
          };
      }
    });
  }, [currentTemplate]);

  const setTemplateDefaults = useCallback((template: string) => {
    console.log('🔄🔄🔄 [TEMPLATE-DEFAULTS] setTemplateDefaults called with template:', template);
    console.log('🔄 [TEMPLATE-DEFAULTS] Current settings before reset:', JSON.stringify(settings, null, 2));
    setCurrentTemplate(template);
    switch (template) {
      case 'corporate':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting CORPORATE_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, CORPORATE_DEFAULTS));
        break;
      case 'footer':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting FOOTER_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, FOOTER_DEFAULTS));
        break;
      case 'classic':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting CLASSIC_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, CLASSIC_DEFAULTS));
        console.log('🔄 [TEMPLATE-DEFAULTS] CLASSIC_DEFAULTS:', JSON.stringify(CLASSIC_DEFAULTS, null, 2));
        break;
      case 'strip':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting STRIP_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, STRIP_DEFAULTS));
        break;
      case 'horizontal':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting HORIZONTAL_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, HORIZONTAL_DEFAULTS));
        break;
      case 'wide':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting WIDE_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, WIDE_DEFAULTS));
        break;
      case 'compact':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting COMPACT_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, COMPACT_DEFAULTS));
        break;
      case 'stacked':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting STACKED_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, STACKED_DEFAULTS));
        break;
      case 'social':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting SOCIAL_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, SOCIAL_DEFAULTS));
        break;
      case 'central':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting CENTRAL_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, CENTRAL_DEFAULTS));
        break;
      case 'topbar':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting TOPBAR_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, TOPBAR_DEFAULTS));
        break;
      case 'rightside':
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting RIGHTSIDE_DEFAULTS');
        setSettings(enforceTemplateThemeDefaults(template, RIGHTSIDE_DEFAULTS));
        break;
      default:
        console.log('🔄 [TEMPLATE-DEFAULTS] Setting DEFAULT_DESIGN_SETTINGS (fallback)');
        setSettings(enforceTemplateThemeDefaults(template, DEFAULT_DESIGN_SETTINGS));
    }
    console.log('🔄🔄🔄 [TEMPLATE-DEFAULTS] setTemplateDefaults completed');
  }, [settings]);

  /**
   * Set template defaults while preserving user content
   * This preserves: personal info, company info, social links, profile image
   * But resets: all styling properties to the new template's defaults
   */
  const setTemplateDefaultsPreserveContent = useCallback((template: string) => {
    console.log('🔄🔄🔄 TEMPLATE_SWITCH setTemplateDefaultsPreserveContent called with template:', template);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH Current settings before template change:', settings);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH Current layoutType:', settings.layoutType);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH Current themeColor:', settings.themeColor);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH Current socialMediaIconType:', settings.socialMediaIconType);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH Current farewellSignature before template change:', settings.farewellSignature);

    if (currentTemplate !== template) {
      // Create a temporary notification element
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #4CAF50;
        color: white;
        padding: 12px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        font-family: Arial, sans-serif;
        font-size: 14px;
        font-weight: 500;
        transform: translateX(100%);
        transition: transform 0.3s ease;
      `;
      notification.textContent = `✅ Switched to ${template.charAt(0).toUpperCase() + template.slice(1)} template`;
      
      document.body.appendChild(notification);
      
      // Animate in
      setTimeout(() => {
        notification.style.transform = 'translateX(0)';
      }, 100);
      
      // Remove after 3 seconds
      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 3000);
    }
    
    setCurrentTemplate(template);
    
    // Get the new template's default styling
    let newTemplateDefaults: DesignSettings;
    switch (template) {
      case 'corporate':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, CORPORATE_DEFAULTS);
        break;
      case 'footer':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, FOOTER_DEFAULTS);
        break;
      case 'classic':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, CLASSIC_DEFAULTS);
        break;
      case 'strip':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, STRIP_DEFAULTS);
        break;
      case 'horizontal':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, HORIZONTAL_DEFAULTS);
        break;
      case 'wide':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, WIDE_DEFAULTS);
        break;
      case 'compact':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, COMPACT_DEFAULTS);
        break;
      case 'stacked':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, STACKED_DEFAULTS);
        break;
      case 'social':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, SOCIAL_DEFAULTS);
        break;
      case 'central':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, CENTRAL_DEFAULTS);
        break;
      case 'topbar':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, TOPBAR_DEFAULTS);
        break;
      case 'rightside':
        newTemplateDefaults = enforceTemplateThemeDefaults(template, RIGHTSIDE_DEFAULTS);
        break;
      default:
        newTemplateDefaults = enforceTemplateThemeDefaults(template, DEFAULT_DESIGN_SETTINGS);
    }

    console.log('🔄🔄🔄 TEMPLATE_SWITCH New template defaults:', newTemplateDefaults);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH New template defaults layoutType:', newTemplateDefaults.layoutType);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH New template defaults themeColor:', newTemplateDefaults.themeColor);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH New template defaults socialMediaIconType:', newTemplateDefaults.socialMediaIconType);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH New template defaults farewellSignature:', newTemplateDefaults.farewellSignature);

    // Preserve farewellSignature from current settings if it exists
    const preservedFarewellSignature = settings.farewellSignature;
    
    if (preservedFarewellSignature) {
      console.log('🔄🔄🔄 TEMPLATE_SWITCH Preserving farewellSignature:', preservedFarewellSignature);
      
      newTemplateDefaults = {
        ...newTemplateDefaults,
        farewellSignature: preservedFarewellSignature
      };
      console.log('🔄🔄🔄 TEMPLATE_SWITCH Final template defaults with preserved content:', newTemplateDefaults);
    } else {
      console.log('🔄🔄🔄 TEMPLATE_SWITCH ⚠️ No farewellSignature to preserve from current settings');
    }

    // Apply the new template's styling defaults
    console.log('🔄🔄🔄 TEMPLATE_SWITCH About to call setSettings with:', newTemplateDefaults);
    setSettings(newTemplateDefaults);
    console.log('🔄🔄🔄 TEMPLATE_SWITCH setSettings called - template change complete');
  }, [settings, currentTemplate]);

  return (
    <DesignSettingsContext.Provider value={{ 
      settings, 
      updateSettings, 
      resetSettings, 
      setTemplateDefaults,
      setTemplateDefaultsPreserveContent 
    }}>
      {children}
    </DesignSettingsContext.Provider>
  );
}; 